/**
 * Session management.
 *
 * Google sign-in goes through Supabase Auth. The generated access token is the
 * only thing the Worker trusts; the browser's own routing is a convenience.
 *
 * A local-development sign-in path exists for offline work and for the test
 * suite. It is refused unless the Worker is explicitly configured for it
 * (DEV_AUTH_ENABLED=true and ENVIRONMENT != production).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session, SupabaseClient } from '@supabase/supabase-js';
import { webConfig } from './config';
import { api } from './api';

const DEV_TOKEN_KEY = 'ard.devToken';

export type SessionStatus = 'loading' | 'signed_out' | 'signed_in' | 'unconfigured';

export interface SessionUser {
  id: string;
  email: string | null;
  displayName: string | null;
  avatarUrl: string | null;
}

export interface SessionContextValue {
  status: SessionStatus;
  user: SessionUser | null;
  mode: 'supabase' | 'dev' | 'none';
  getToken: () => Promise<string | null>;
  signInWithGoogle: (nextPath?: string) => Promise<void>;
  signInWithDevSession: () => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

let supabaseSingleton: SupabaseClient | null = null;

async function getSupabaseClient(): Promise<SupabaseClient | null> {
  if (!webConfig.supabaseConfigured) return null;
  if (supabaseSingleton) return supabaseSingleton;
  const { createClient } = await import('@supabase/supabase-js');
  supabaseSingleton = createClient(webConfig.supabaseUrl, webConfig.supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  });
  return supabaseSingleton;
}

function userFromSession(session: Session | null): SessionUser | null {
  if (!session?.user) return null;
  const metadata = (session.user.user_metadata ?? {}) as Record<string, unknown>;
  return {
    id: session.user.id,
    email: session.user.email ?? null,
    displayName:
      typeof metadata.full_name === 'string'
        ? metadata.full_name
        : typeof metadata.name === 'string'
          ? metadata.name
          : null,
    avatarUrl: typeof metadata.avatar_url === 'string' ? metadata.avatar_url : null,
  };
}

function readDevToken(): string | null {
  try {
    return window.sessionStorage.getItem(DEV_TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeDevToken(token: string | null): void {
  try {
    if (token) window.sessionStorage.setItem(DEV_TOKEN_KEY, token);
    else window.sessionStorage.removeItem(DEV_TOKEN_KEY);
  } catch {
    // ignore
  }
}

/** Decode the payload of a JWT for display only. Never used for authorisation. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const padded = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>(() =>
    webConfig.supabaseConfigured || webConfig.devAuthEnabled ? 'loading' : 'unconfigured',
  );
  const [user, setUser] = useState<SessionUser | null>(null);
  const [mode, setMode] = useState<'supabase' | 'dev' | 'none'>(
    webConfig.supabaseConfigured ? 'supabase' : webConfig.devAuthEnabled ? 'dev' : 'none',
  );
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    (async () => {
      if (webConfig.supabaseConfigured) {
        const client = await getSupabaseClient();
        if (!client || cancelled) return;
        const { data } = await client.auth.getSession();
        if (cancelled) return;
        if (data.session) {
          setUser(userFromSession(data.session));
          setStatus('signed_in');
          setMode('supabase');
        } else {
          setUser(null);
          setStatus('signed_out');
        }

        const { data: subscription } = client.auth.onAuthStateChange((_event, session) => {
          setUser(userFromSession(session));
          setStatus(session ? 'signed_in' : 'signed_out');
        });
        unsubscribe = () => subscription.subscription.unsubscribe();
        return;
      }

      if (webConfig.devAuthEnabled) {
        const token = readDevToken();
        if (cancelled) return;
        if (token) {
          const payload = decodeJwtPayload(token);
          const exp = typeof payload?.exp === 'number' ? payload.exp * 1000 : 0;
          if (exp > Date.now()) {
            setUser({
              id: typeof payload?.sub === 'string' ? payload.sub : 'dev',
              email: typeof payload?.email === 'string' ? payload.email : null,
              displayName: 'Local Developer',
              avatarUrl: null,
            });
            setMode('dev');
            setStatus('signed_in');
            return;
          }
          writeDevToken(null);
        }
        setUser(null);
        setStatus('signed_out');
        return;
      }

      setStatus('unconfigured');
    })().catch(() => {
      if (!cancelled) setStatus('signed_out');
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [generation]);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (mode === 'dev') return readDevToken();
    if (!webConfig.supabaseConfigured) return null;
    const client = await getSupabaseClient();
    if (!client) return null;
    const { data } = await client.auth.getSession();
    if (data.session) return data.session.access_token;
    const refreshed = await client.auth.refreshSession();
    return refreshed.data.session?.access_token ?? null;
  }, [mode]);

  const signInWithGoogle = useCallback(async (nextPath?: string) => {
    const client = await getSupabaseClient();
    if (!client) {
      throw new Error('Supabase is not configured, so Google sign-in is unavailable.');
    }
    const redirectTo = `${window.location.origin}${nextPath ?? '/app'}`;
    const { error } = await client.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo, queryParams: { prompt: 'select_account' } },
    });
    if (error) throw error;
  }, []);

  const signInWithDevSession = useCallback(async () => {
    const result = await api.devSession();
    writeDevToken(result.token);
    setUser({
      id: result.user.id,
      email: result.user.email,
      displayName: 'Local Developer',
      avatarUrl: null,
    });
    setMode('dev');
    setStatus('signed_in');
  }, []);

  const signOut = useCallback(async () => {
    if (mode === 'supabase') {
      const client = await getSupabaseClient();
      await client?.auth.signOut();
    }
    writeDevToken(null);
    setUser(null);
    setStatus('signed_out');
  }, [mode]);

  const refresh = useCallback(() => setGeneration((value) => value + 1), []);

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      user,
      mode,
      getToken,
      signInWithGoogle,
      signInWithDevSession,
      signOut,
      refresh,
    }),
    [status, user, mode, getToken, signInWithGoogle, signInWithDevSession, signOut, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside SessionProvider');
  return context;
}
